
import React, { useState, useEffect } from 'react';
import Icon from './Icon';
import ConfirmationModal from './ConfirmationModal';
import { useLanguage } from '../context/LanguageContext';

interface ImageDisplayProps {
    imageUrl: string | null;
    isLoading: boolean;
    onDelete?: () => void;
    isLocked?: boolean;
    onUnlock?: () => void;
}

const LOADING_TIPS = [
    "Analyzing metal texture...",
    "Calculating light reflections...",
    "Polishing gemstones...",
    "Rendering 8k textures...",
    "Applying studio lighting...",
    "Finalizing details..."
];

const ImageDisplay: React.FC<ImageDisplayProps> = ({ imageUrl, isLoading, onDelete, isLocked = false, onUnlock }) => {
    const { t } = useLanguage();
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [tipIndex, setTipIndex] = useState(0);

    // Rotate tips every 2.5 seconds to make wait feel shorter
    useEffect(() => {
        if (!isLoading) return;
        const interval = setInterval(() => {
            setTipIndex((prev) => (prev + 1) % LOADING_TIPS.length);
        }, 2500);
        return () => clearInterval(interval);
    }, [isLoading]);

    const downloadImage = () => {
        if (!imageUrl || isLocked) return;
        const link = document.createElement('a');
        link.href = imageUrl;
        link.download = `najeweller-ai-${Date.now()}.png`;
        document.body.appendChild(link);
        link.click();
        link.remove();
    };
    
    const generalShare = async () => {
        if (!imageUrl || isLocked) return;
        try {
            const response = await fetch(imageUrl);
            const blob = await response.blob();
            const file = new File([blob], `najeweller-ai-${Date.now()}.png`, { type: blob.type });
            const shareText = `${t('shareText')}\nhttps://najeweller.in`;
            
            if (navigator.canShare && navigator.canShare({ files: [file] })) {
                await navigator.share({
                    files: [file],
                    title: t('appTitle'),
                    text: shareText,
                });
            }
        } catch (error) {
            console.error('Error sharing:', error);
        }
    };

    const canShare = typeof navigator !== 'undefined' && !!navigator.share;

    return (
        <div className="flex flex-col w-full h-full gap-3">
            <div className="flex-1 relative w-full min-h-0 bg-slate-50 dark:bg-black/40 rounded-apple flex items-center justify-center overflow-hidden group border border-black/5 dark:border-white/10 shadow-inner">
                
                {/* Shimmer Loading Effect */}
                {isLoading && (
                    <div className="absolute inset-0 z-10 flex flex-col items-center justify-center">
                        {/* Shimmer Background */}
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 dark:via-white/5 to-transparent -translate-x-full animate-[shimmer_1.5s_infinite]"></div>
                        
                        <div className="relative z-20 flex flex-col items-center gap-6">
                            <div className="relative">
                                <div className="w-20 h-20 rounded-full border-4 border-slate-200 dark:border-white/10 opacity-30 animate-ping absolute inset-0"></div>
                                <div className="w-20 h-20 rounded-full bg-slate-100 dark:bg-white/5 flex items-center justify-center relative overflow-hidden shadow-2xl">
                                    <Icon name="sparkles" className="w-8 h-8 text-apple-gold animate-pulse" />
                                    <div className="absolute inset-0 bg-gradient-to-tr from-apple-gold/20 to-transparent animate-spin-slow"></div>
                                </div>
                            </div>
                            
                            <div className="flex flex-col items-center gap-2">
                                <p className="font-bold text-sm text-slate-900 dark:text-white uppercase tracking-widest animate-pulse">
                                    Creating Magic
                                </p>
                                <p className="text-xs text-slate-400 dark:text-slate-500 font-medium transition-all duration-500 min-h-[1.5em]">
                                    {LOADING_TIPS[tipIndex]}
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {!isLoading && !imageUrl && (
                    <div className="text-center space-y-4 px-10">
                        <div className="w-20 h-20 mx-auto rounded-full bg-slate-100 dark:bg-white/5 flex items-center justify-center text-slate-300 dark:text-white/10 border border-black/5 dark:border-white/5">
                            <Icon name="diamond" className="w-8 h-8 opacity-50" />
                        </div>
                        <p className="text-slate-300 dark:text-slate-600 text-lg font-light">{t('imageDisplayPlaceholder')}</p>
                    </div>
                )}

                {imageUrl && !isLoading && (
                    <>
                        <img 
                            src={imageUrl} 
                            alt="Generated design" 
                            className={`w-full h-full object-contain animate-in fade-in zoom-in duration-700 transition-all ${isLocked ? 'blur-md scale-105 opacity-60' : ''}`} 
                        />
                        
                        {isLocked && (
                            <div className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-black/10 backdrop-blur-md animate-in fade-in zoom-in duration-500">
                                <div className="text-center space-y-4 p-8 bg-white/90 dark:bg-black/80 apple-blur rounded-3xl shadow-2xl border border-white/20 max-w-xs mx-4">
                                    <div className="w-14 h-14 rounded-full bg-apple-gold/10 flex items-center justify-center mx-auto mb-2 text-apple-gold">
                                        <Icon name="lock" className="w-6 h-6" />
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-1">HD Mockup Ready</h3>
                                        <p className="text-xs text-slate-500 font-medium">Subscribe to view high-definition results</p>
                                    </div>
                                    <button 
                                        onClick={onUnlock}
                                        className="w-full py-3 bg-apple-gold text-white rounded-full font-bold text-sm hover:brightness-110 transition-all shadow-lg active:scale-95"
                                    >
                                        Unlock Now
                                    </button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Action Buttons Row - Outside the image for mobile usability */}
            {imageUrl && !isLoading && !isLocked && (
                <div className="flex items-center justify-center gap-3 shrink-0 animate-in slide-in-from-bottom-1 fade-in">
                    <button
                        onClick={downloadImage}
                        className="h-10 px-5 flex items-center justify-center gap-2 bg-white dark:bg-white/10 text-slate-700 dark:text-slate-200 rounded-full border border-black/5 dark:border-white/10 hover:bg-slate-50 dark:hover:bg-white/20 transition-all shadow-sm active:scale-95"
                        title={t('downloadButton')}
                    >
                        <Icon name="download" className="w-4 h-4" />
                        <span className="text-xs font-bold hidden sm:inline">{t('downloadButton')}</span>
                    </button>
                    
                    {canShare && (
                        <button
                            onClick={generalShare}
                            className="h-10 w-10 flex items-center justify-center bg-white dark:bg-white/10 text-slate-700 dark:text-slate-200 rounded-full border border-black/5 dark:border-white/10 hover:bg-slate-50 dark:hover:bg-white/20 transition-all shadow-sm active:scale-95"
                            title={t('shareButton')}
                        >
                            <Icon name="share" className="w-4 h-4" />
                        </button>
                    )}

                    {onDelete && (
                         <button
                            onClick={() => setShowDeleteConfirm(true)}
                            className="h-10 w-10 flex items-center justify-center bg-red-50 dark:bg-red-500/10 text-red-500 rounded-full border border-red-200 dark:border-red-500/30 hover:bg-red-100 dark:hover:bg-red-500/20 transition-all shadow-sm active:scale-95"
                            title={t('deleteButton')}
                        >
                            <Icon name="trash" className="w-4 h-4" />
                        </button>
                    )}
                </div>
            )}

            <ConfirmationModal 
                isOpen={showDeleteConfirm}
                onClose={() => setShowDeleteConfirm(false)}
                onConfirm={onDelete || (() => {})}
                title={t('confirmDeleteImageTitle')}
                message={t('confirmDeleteImageMessage')}
                confirmLabel={t('deleteButton')}
                variant="danger"
            />
        </div>
    );
};

export default ImageDisplay;
